import { useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  Button,
  Center,
  Flex,
  Spinner,
  Text,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';

import { ArrowLeftOutline } from '@aslanpay/portal-common-lib';

import { AuthIntegrationTab } from 'app/components/Settings/AuthIntegrationTab';
import { FeatureModulesTab } from 'app/components/Settings/FeatureModulesTab';
import { FileUploadTab } from 'app/components/Settings/FileUploadTab';
import { GeneralTab } from 'app/components/Settings/GeneralTab';
import { NotificationsTab } from 'app/components/Settings/NotificationsTab';
import { ReportsTab } from 'app/components/Settings/ReportsTab';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';

import useFetchCompany from 'app/hooks/useFetchCompany';

type TabLabel =
  | 'general'
  | 'feature-modules'
  | 'reports'
  | 'file-upload'
  | 'notifications'
  | 'auth-integration';

interface TabsConfig {
  index: number;
  label: TabLabel;
  displayName: string;
  visible: boolean;
}

function Settings() {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const { companyId } = useParams();

  const { data: company, isInitialLoading: isLoadingCompany } = useFetchCompany(companyId);

  const tabsConfig: TabsConfig[] = useMemo(() => {
    return [
      {
        index: 0,
        label: 'general',
        displayName: 'General',
        visible: true,
      },
      {
        index: 1,
        label: 'feature-modules',
        displayName: 'Feature Modules',
        visible: true,
      },
      {
        index: 2,
        label: 'reports',
        displayName: 'Reports',
        visible: true,
      },
      {
        index: 3,
        label: 'file-upload',
        displayName: 'File Upload & SFTP',
        visible: true,
      },
      {
        index: 4,
        label: 'notifications',
        displayName: 'Notifications',
        visible: true,
      },
      {
        index: 5,
        label: 'auth-integration',
        displayName: 'Auth & Integration',
        visible: true,
      },
    ];
  }, []);

  const currentTabLabel = useMemo(() => {
    const tabParam = searchParams.get('tab');
    const isValid = tabParam && tabsConfig.some((tab) => tab.label === tabParam);
    return isValid ? (tabParam as TabLabel) : tabsConfig[0].label;
  }, [searchParams, tabsConfig]);

  const [tabLabel, setTabLabel] = useState(currentTabLabel);

  const visibleTabs = useMemo(() => tabsConfig.filter((tab) => tab.visible), [tabsConfig]);

  useEffect(() => {
    setTabLabel(currentTabLabel);
  }, [currentTabLabel]);

  const handleTabsChange = (index: number) => {
    const newTabLabel = visibleTabs[index].label;
    setTabLabel(newTabLabel);
    setSearchParams({ tab: newTabLabel });
  };

  const renderTabContent = (tab: TabsConfig) => {
    if (tab.label === 'general') {
      return <GeneralTab companyId={company?.id} />;
    }

    if (tab.label === 'feature-modules') {
      return <FeatureModulesTab companyId={company?.id} />;
    }

    if (tab.label === 'reports') {
      return <ReportsTab companyId={company?.id} />;
    }

    if (tab.label === 'file-upload') {
      return <FileUploadTab companyId={company?.id} />;
    }

    if (tab.label === 'notifications') {
      return <NotificationsTab companyId={company?.id} />;
    }

    if (tab.label === 'auth-integration') {
      return <AuthIntegrationTab companyId={company?.id} />;
    }

    return null;
  };

  return (
    <AuthenticatedPageWrapper title="Settings">
      {isLoadingCompany ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <>
          <Button
            my="10px"
            alignSelf="flex-start"
            size="sm"
            variant="link"
            leftIcon={<ArrowLeftOutline boxSize="20px" />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Flex gap="20px" align="flex-start" justify="space-between">
            <Text as="h2" textStyle="h2" color="brand.darkPurple">
              {company?.name} - Settings
            </Text>
          </Flex>

          <Tabs
            w="full"
            mt="10px"
            index={visibleTabs.findIndex((tab) => tab.label === tabLabel)}
            onChange={handleTabsChange}
          >
            <TabList>
              {visibleTabs.map((tab) => (
                <Tab key={tab.label}>{tab.displayName}</Tab>
              ))}
            </TabList>

            <TabPanels mt="40px">
              {visibleTabs.map((tab) => (
                <TabPanel key={tab.label} w="full" px="0">
                  {renderTabContent(tab)}
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </>
      )}
    </AuthenticatedPageWrapper>
  );
}

export default Settings;
